import { sendRequest } from "@infrastructure/api/api";
import { InvitationResponse } from "@features/invitations/types/invitation";
import ApiResponse from "@core/types/apiResponse";
import { ApiMessageResponse } from "@core/types/apiMessageResponse";

const toQueryString = (params: Record<string, any>) => {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        query.set(key, String(value));
    });

    return query.toString();
};

export type FetchMyInvitationsParams = {
    query?: string;
    status?: string;
    page?: number;
    perPage?: number;
};

/**
 * Fetches the list of pending invitations for the current user.
 *
 * @param params - An object containing query parameters to filter or paginate the invitations.
 * @returns A promise that resolves to an `ApiResponse` containing the invitation response data.
 *
 * @example
 * ```typescript
 * const response = await fetchMyInvitations({ page: 1, perPage: 10 });
 * ```
 */
export const fetchMyInvitations = async (
    params?: FetchMyInvitationsParams
): Promise<ApiResponse<InvitationResponse>> => {
    const query = toQueryString(params ?? {});
    const response = await sendRequest<any, InvitationResponse>('GET', `/user/invite/pending${query ? `?${query}` : ''}`);
    return response;
}

/**
 * Accepts an invitation by its ID.
 *
 * Sends a POST request to the `/invite/accept/:id` endpoint to accept the invitation.
 *
 * @param id - The unique identifier of the invitation to accept.
 * @returns A promise that resolves to an `ApiResponse` containing the server's response.
 */
export const acceptInvitation = async (id: number): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<any, ApiMessageResponse>('POST', `/user/invite/accept/${id}`);
    return response;
}

/**
 * Sends a request to reject an invitation by its ID.
 *
 * @param id - The unique identifier of the invitation to reject.
 * @returns A promise that resolves to an ApiResponse containing the result of the rejection operation.
 */
export const rejectInvitation = async (id: number): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<any, ApiMessageResponse>('POST', `/user/invite/reject/${id}`);
    return response;
}
